import { Tabs, TabsList, TabsTrigger } from "@gearbox-protocol/deploy-tools-ui";
import { type FC, type ReactNode, useMemo } from "react";
import { Link, matchPath, resolvePath, useLocation } from "react-router-dom";

export interface NavBarItem {
  title: ReactNode;
  path: string;
  disabled?: boolean;
  /**
   * Requires id to be set on route and this id must be same as path here
   */
  hideEntireBar?: boolean;
}

interface NavBarProps {
  pattern: string;
  items: NavBarItem[];
  className?: string;
  buttonClassName?: string;
}

const NavBar: FC<NavBarProps> = props => {
  const { items, className, buttonClassName, pattern } = props;
  const { pathname } = useLocation();

  const { navItems, activeItem } = useMemo(() => {
    const base =
      matchPath({ path: pattern, end: false }, pathname)?.pathname ?? "/";
    const navItems = items.map(item => {
      return {
        ...item,
        path: resolvePath(item.path, base).pathname,
      };
    });
    const activeItem = navItems.find(i => {
      return pathname.startsWith(i.path);
    });
    return {
      navItems,
      activeItem,
    };
  }, [pattern, pathname, items]);

  if (activeItem?.hideEntireBar) {
    return null;
  }

  return (
    <Tabs value={activeItem?.path} className={className}>
      <TabsList>
        {navItems.map(i => (
          <NavBarButton key={i.path} item={i} className={buttonClassName} />
        ))}
      </TabsList>
    </Tabs>
  );
};

interface NavBarButtonProps {
  item: NavBarItem;
  className?: string;
}

const NavBarButton: FC<NavBarButtonProps> = ({ item, className }) => {
  if (item.hideEntireBar) {
    return null;
  }

  return (
    <TabsTrigger
      className={className}
      key={item.path}
      value={item.path}
      disabled={item.disabled}
      asChild
    >
      <Link to={item.path}>{item.title}</Link>
    </TabsTrigger>
  );
};

export default NavBar;
